import { useSubscription } from "../../hooks/useSubscription";
import PremiumUpgradeModal from "../PremiumUpgradeModal";
import React, { useEffect, useState } from"react";
import { useNavigate } from"react-router-dom";
import { useTenant } from"../../Context/TenantContext";
import axios from"axios";
import * as pdfjsLib from"pdfjs-dist";
import mammoth from"mammoth";
import API_BASE_URL from"../../Config/config";
import { checkClickStatus, recordButtonClick, hasUnlimitedClicks, BUTTON_TYPES } from"../../utils/mockInterviewClickTracker";

// Use local pdf.worker.min.js from CDN to avoid local fetch issues
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const BASE_LS_KEY ="mock_interview_state_v1";
const getLsKey = (userId) => `${BASE_LS_KEY}:${userId || 'guest'}`;
const defaultState = {
 plan:"free",
 usedTrials: {
 coding: false,
 technical: false,
 hr: false,
 arithmetic: false,
 time_management: false,
 },
 formData: {
 name:"",
 email:"",
 position:"student",
 experience:"",
 skills:"",
 resumeName:"",
 jobDescription:"",
 projects:"",
 },
};

function loadStateForUser(userId) {
 try {
 const raw = localStorage.getItem(getLsKey(userId));
 if (!raw) return defaultState;
 const parsed = JSON.parse(raw);
 return {
 ...defaultState,
 plan: parsed.plan ||"free",
 usedTrials: parsed.usedTrials || defaultState.usedTrials,
 // formData will be set from user on mount; do not persist old entries
 };
 } catch {
 return defaultState;
 }
}

function saveStateForUser(userId, s) {
 try {
 const toSave = { plan: s.plan, usedTrials: s.usedTrials };
 localStorage.setItem(getLsKey(userId), JSON.stringify(toSave));
 } catch {}
}

export default function MockInterviewHome({ mode = "learner" }) {
  const navigate = useNavigate();
  const { tenant, getCollegeName, getLogoUrl } = useTenant();
  const { isPaid, usage, isLoaded, canAccessFeature, plan, expiry, daysRemaining, amount, refresh, limits } = useSubscription();

  const isTenantMode = mode === "tenant" || (!!tenant && window.location.hostname !== 'theskillgenie.com' && window.location.hostname !== 'www.theskillgenie.com' && window.location.hostname !== 'localhost');
  const collegeName = getCollegeName();
  const collegeLogo = getLogoUrl();

  const [userId, setUserId] = useState(() => {
    try {
      const userRaw = localStorage.getItem("user");
      const u = userRaw ? JSON.parse(userRaw) : null;
      return (u?.userId || "guest");
    } catch { return "guest"; }
  });
  const [state, setState] = useState(() => loadStateForUser(userId));
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);

  const backPath = '/dashboard';

  // Load user info on mount and restore saved form data
  useEffect(() => {
    const userRaw = localStorage.getItem("user");
    const savedFormDataKey = `mock_interview_form_data_${userId}`;
    const savedFormData = localStorage.getItem(savedFormDataKey);
    
    if (userRaw) {
      const user = JSON.parse(userRaw);
      let formDataToUse = {
        name: user.name || "",
        email: user.email || "",
        position: "",
        experience: "",
        skills: "",
        resumeName: "",
        jobDescription: "",
        projects: "",
      };
      
      // Restore saved form data if available
      if (savedFormData) {
        try {
          const parsed = JSON.parse(savedFormData);
          formDataToUse = {
            ...formDataToUse,
            ...parsed,
            // Always use current user name/email
            name: user.name || parsed.name || "",
            email: user.email || parsed.email || "",
          };
        } catch (err) {
          console.error("Error parsing saved form data:", err);
        }
      }
      
      setState((s) => ({
        ...s,
        formData: formDataToUse,
      }));
    }
  }, [userId]);

  useEffect(() => { saveStateForUser(userId, state); }, [userId, state.plan, state.usedTrials]);

  // When user switches accounts, load that user's persisted trial state
  useEffect(() => {
    try {
      const userRaw = localStorage.getItem("user");
      const u = userRaw ? JSON.parse(userRaw) : null;
      const id = (u?.userId || "guest");
      setUserId(id);
      setState((prev) => ({
        ...loadStateForUser(id),
        formData: prev.formData,
      }));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planSummary = {
    label: isTenantMode ? "Institutional Premium" : (isPaid ? plan || "SkillGenie Premium" : "Free Plan"),
    expiresOn: isTenantMode ? "Never" : (expiry || null),
    daysRemaining: isTenantMode ? 999 : (daysRemaining || null),
    amount: isTenantMode ? 0 : (amount || null),
  };

 const setFormField = (k, v) => {
 setState((s) => {
 const updatedFormData = { ...s.formData, [k]: v };
 // Save form data to localStorage for auto-fill
 const savedFormDataKey = `mock_interview_form_data_${userId}`;
 try {
 localStorage.setItem(savedFormDataKey, JSON.stringify(updatedFormData));
 } catch (err) {
 console.error("Error saving form data:", err);
 }
 return { ...s, formData: updatedFormData };
 });
 };

 // Extract skills example (simple demo)
 function extractSkillsFromText(text) {
 const knownSkills = [
"JavaScript",
"React",
"Node.js",
"Python",
"SQL",
"Java",
"C++",
"AWS",
"Docker",
"Kubernetes",
 ];
 return knownSkills
 .filter((skill) => text.toLowerCase().includes(skill.toLowerCase()))
 .join(",");
 }

 // Handle resume upload and extract text from PDF or DOCX
 const handleResumeUpload = async (e) => {
 const file = e.target.files[0];
 if (!file) {
 alert("No file selected.");
 return;
 }

 const allowedTypes = [
"application/pdf",
"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
 ];
 if (!allowedTypes.includes(file.type)) {
 alert("Please upload a PDF or DOCX file.");
 return;
 }

 const maxSize = 5 * 1024 * 1024; // 5MB
 if (file.size > maxSize) {
 alert("File size exceeds 5MB.");
 return;
 }

 setResumeFile(file);
 setFormField("resumeName", file.name);

 try {
 const fileReader = new FileReader();
 fileReader.onload = async function () {
 try {
 let fullText ="";

 if (file.type ==="application/pdf") {
 // Process PDF
 console.log("Starting PDF processing...");
 try {
 const typedArray = new Uint8Array(this.result);
 const pdf = await pdfjsLib.getDocument({ data: typedArray, disableWorker: false }).promise;
 console.log("PDF loaded successfully, pages:", pdf.numPages);

 for (let i = 1; i <= pdf.numPages; i++) {
 const page = await pdf.getPage(i);
 const content = await page.getTextContent();
 const strings = content.items.map((item) => item.str);
 fullText += strings.join("") +"\n";
 }
 } catch (pdfError) {
 console.warn("PDF worker failed, falling back to no worker:", pdfError);
 // Fallback: Disable worker and try again
 const typedArray = new Uint8Array(this.result);
 const pdf = await pdfjsLib.getDocument({ data: typedArray, disableWorker: true }).promise;
 console.log("PDF loaded successfully without worker, pages:", pdf.numPages);

 for (let i = 1; i <= pdf.numPages; i++) {
 const page = await pdf.getPage(i);
 const content = await page.getTextContent();
 const strings = content.items.map((item) => item.str);
 fullText += strings.join("") +"\n";
 }
 }
 } else if (
 file.type ===
"application/vnd.openxmlformats-officedocument.wordprocessingml.document"
 ) {
 // Process DOCX
 console.log("Starting DOCX processing...");
 const arrayBuffer = this.result;
 const result = await mammoth.extractRawText({ arrayBuffer });
 fullText = result.value;
 console.log("DOCX processed successfully");
 }

 console.log("Extracted Resume Text:", fullText);

 // Extract and store skills
 const extractedSkills = extractSkillsFromText(fullText);
 if (extractedSkills) {
 setFormField("skills", extractedSkills);
 console.log("Stored Skills:", extractedSkills);
 } else {
 console.log("No known skills found in resume.");
 setFormField("skills","");
 }

  // Extract experience
  // We'll no longer auto-fill experience to prevent overwhelming the small input field
  setFormField("experience", "");
  console.log("Auto-filling experience is disabled.");

  // Extract projects
  // We'll no longer auto-fill projects as it can be too much text for the form
  setFormField("projects", "");
  console.log("Auto-filling projects is disabled.");

 alert("Resume processed successfully!");
 } catch (err) {
 console.error("Error processing file:", err);
 alert(`Failed to process file. Please try another file or contact support. Error: ${err.message}`);
 }
 };

 fileReader.onerror = () => {
 console.error("FileReader error:", fileReader.error);
 alert("Failed to read the file. Please ensure the file is not corrupted.");
 };

 fileReader.readAsArrayBuffer(file);
 } catch (err) {
 console.error("Error in handleResumeUpload:", err);
 alert(`Failed to process resume. Please try again or contact support. Error: ${err.message}`);
 }
 };

 // Free plan: allow one-time access to ALL 5 types (no plan-based lock initially)
 // Basic: lock arithmetic; Non-pro: lock time_management
 const isLockedByPlan = (plan, type) => {
 if (plan ==="free") return false;
 if (plan ==="basic" && type ==="arithmetic") return true;
 if (plan !=="pro" && type ==="time_management") return true;
 return false;
 };

 const formatPlanDate = (value) => {
 if (!value) return null;
 return new Date(value).toLocaleDateString("en-IN", {
 year:"numeric",
 month:"short",
 day:"numeric",
 });
 };

   const wrapperClick = (type) => {
    // ✅ FULL ACCESS FOR TENANT
    if (isTenantMode) {
      // Use /student prefix for tenant student flow to match routes.jsx
      const path = type === "discount" ? "/discount-interview" : `/student/mockinterview/${type}`;
      navigate(path, {
        state: { formData: state.formData, resumeFile: resumeFile?.name },
      });
      return;
    }

    // ✅ Centralized access check using SubscriptionContext
    if (type === "discount" || canAccessFeature('MOCK_INTERVIEW', { type })) {
      const path = type === "discount" ? "/discount-interview" : `/mockinterview/${type}`;
      navigate(path, {
        state: { formData: state.formData, resumeFile: resumeFile?.name },
      });
      return;
    }

    // If access denied
    setSelectedType(type);
    setShowModal(true);
  };

  return (
<div className="min-h-screen bg-bgMain text-body relative p-4 font-sans overflow-hidden">
 <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>

 {/* Back button positioned top-left independently */}
<button
onClick={() => navigate(backPath)}
className="absolute top-6 left-6 bg-bgSection text-primary px-6 py-2 rounded-full font-bold border border-primary/30 hover:bg-primary hover:text-white transition-all shadow-lg z-10 uppercase text-xs tracking-widest"
>
← Back
</button>

 {/* Subscription Countdown - Only for paid users */}
 {daysRemaining !== null && (
    <div className="absolute top-6 right-6 flex items-center gap-2 bg-primary/10 px-4 py-1.5 rounded-xl border border-primary/20 animate-pulse z-10">
        <span className="text-primary text-sm font-bold">
            ⏳ {daysRemaining} days left
        </span>
    </div>
 )}

 {/* Centered Card */}
 <div className="max-w-4xl mx-auto pt-12 pb-12">
 <div className="w-full bg-bgSection shadow-2xl p-8 md:p-12 rounded-[48px] border border-primary/30 relative overflow-hidden z-10"
 style={{ boxShadow:"0 25px 60px rgba(0, 0, 0, 0.4)" }}>

 <h2 className="text-4xl md:text-5xl font-black mb-10 text-center bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent uppercase tracking-tighter">
  {isTenantMode ? "Mock Interview" : "Mock Interview"}
</h2>

 <div className="flex flex-col gap-5 mb-10">
 <div className="space-y-1">
 <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Candidate Name</label>
 <input
 value={state.formData.name}
 disabled
 className="w-full p-4 bg-bgMain rounded-2xl border border-accentGray/30 text-muted opacity-75 cursor-not-allowed font-medium text-sm"
 title="Name is loaded from your profile"
 />
 </div>

 <div className="space-y-1">
 <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Email Address</label>
 <input
 value={state.formData.email}
 disabled
 className="w-full p-4 bg-bgMain rounded-2xl border border-accentGray/30 text-muted opacity-75 cursor-not-allowed font-medium text-sm"
 title="Email is loaded from your profile"
 />
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
 <div className="space-y-1">
 <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Target Position</label>
 <select
 value={state.formData.position}
 onChange={(e) => setFormField("position", e.target.value)}
 className="w-full p-4 bg-bgMain rounded-2xl border border-accentGray/30 text-body focus:border-primary outline-none transition-all font-medium text-sm cursor-pointer appearance-none"
 title="Select your current position or role"
 style={{ backgroundImage:"url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')", backgroundRepeat:"no-repeat", backgroundPosition:"right 1rem center", backgroundSize:"1.2em" }}
 >
 <option value="" className="bg-bgSection">Select Position</option>
 <option value="Frontend Developer" className="bg-bgSection">Frontend Developer</option>
 <option value="Backend Developer" className="bg-bgSection">Backend Developer</option>
 <option value="Full Stack Developer" className="bg-bgSection">Full Stack Developer</option>
 <option value="Software Engineer" className="bg-bgSection">Software Engineer</option>
 <option value="Data Scientist" className="bg-bgSection">Data Scientist</option>
 <option value="Data Analyst" className="bg-bgSection">Data Analyst</option>
 <option value="Machine Learning Engineer" className="bg-bgSection">Machine Learning Engineer</option>
 <option value="DevOps Engineer" className="bg-bgSection">DevOps Engineer</option>
 <option value="Cloud Architect" className="bg-bgSection">Cloud Architect</option>
 <option value="QA Engineer" className="bg-bgSection">QA Engineer</option>
 <option value="Mobile App Developer" className="bg-bgSection">Mobile App Developer</option>
 <option value="UI/UX Designer" className="bg-bgSection">UI/UX Designer</option>
 <option value="System Administrator" className="bg-bgSection">System Administrator</option>
 <option value="Cybersecurity Analyst" className="bg-bgSection">Cybersecurity Analyst</option>
 <option value="Business Analyst" className="bg-bgSection">Business Analyst</option>
 <option value="Project Manager" className="bg-bgSection">Project Manager</option>
 <option value="Product Manager" className="bg-bgSection">Product Manager</option>
 <option value="Java Developer" className="bg-bgSection">Java Developer</option>
 <option value="Python Developer" className="bg-bgSection">Python Developer</option>
 <option value="Node.js Developer" className="bg-bgSection">Node.js Developer</option>
 <option value="Fresher / Intern" className="bg-bgSection">Fresher / Intern</option>
 <option value="other" className="bg-bgSection">Other</option>
 </select>
 </div>

 <div className="space-y-1">
 <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Experience Level</label>
 <input
 placeholder="e.g. 2 Years, Fresher"
 value={state.formData.experience}
 onChange={(e) => setFormField("experience", e.target.value)}
 className="w-full p-4 bg-bgMain rounded-2xl border border-accentGray/30 text-body focus:border-primary outline-none transition-all font-medium text-sm"
 title="Enter your work experience or years of experience"
 />
 </div>
 </div>

 <div className="space-y-1">
 <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Key Skills</label>
 <input
 placeholder="React, Python, AWS..."
 value={state.formData.skills}
 onChange={(e) => setFormField("skills", e.target.value)}
 className="w-full p-4 bg-bgMain rounded-2xl border border-accentGray/30 text-body focus:border-primary outline-none transition-all font-medium text-sm"
 title="List your technical skills separated by commas"
 />
 </div>

 <div className="space-y-1">
 <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Upload Resume (PDF/DOCX)</label>
 <div className="relative group">
 <input
 type="file"
 accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
 onChange={handleResumeUpload}
 className="w-full p-3 rounded-2xl bg-bgMain border border-accentGray/30 text-xs file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:bg-primary/10 file:text-primary hover:file:bg-primary/20 transition-all cursor-pointer"
 title="Upload your resume to auto-fill skills, experience, and projects"
 />
 {state.formData.resumeName && (
 <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-primary uppercase tracking-widest bg-bgMain px-2 py-1 rounded-lg">
 📎 {state.formData.resumeName.substring(0, 15)}...
 </span>
 )}
 </div>
 </div>

 <div className="space-y-1">
 <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Key Projects</label>
 <textarea
 placeholder="Describe your major projects..."
 value={state.formData.projects}
 onChange={(e) => setFormField("projects", e.target.value)}
 className="w-full p-4 bg-bgMain rounded-2xl border border-accentGray/30 text-body focus:border-primary outline-none transition-all font-medium text-sm resize-none h-24"
 title="Describe your projects and portfolio work"
 />
 </div>

 <div className="space-y-1">
 <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Job Description (Optional)</label>
 <textarea
 placeholder="Paste the target JD here..."
 value={state.formData.jobDescription}
 onChange={(e) => setFormField("jobDescription", e.target.value)}
 className="w-full p-4 bg-bgMain rounded-2xl border border-accentGray/30 text-body focus:border-primary outline-none transition-all font-medium text-sm resize-none h-24"
 title="Paste the job description you're applying for"
 />
 </div>
 </div>

 {/* Interview Type Buttons */}
<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
    {["technical","hr","coding","time_management","arithmetic", "discount"]
      .filter(type => type !== "discount" || !isTenantMode)
      .map(
      (type) => {
        const hasAccess = type === "discount" || canAccessFeature('MOCK_INTERVIEW', { type });
        const visuallyLocked = !hasAccess;

        const typeName = type === "discount" ? "One-Time Discount" : type.replace(/_/g," ").replace(/\b\w/g, (l) => l.toUpperCase());
        const isSelected = selectedType === type;

        return (
<button
key={type}
onClick={() => wrapperClick(type)}
className={`p-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-300 shadow-lg flex items-center justify-center text-center leading-tight ${
type === "discount" 
  ? "bg-gradient-to-br from-indigo-600 to-purple-600 text-white border-2 border-white/20 hover:scale-110 shadow-indigo-500/30" 
  : visuallyLocked
    ? "bg-white/5 text-muted border border-accentGray/50 cursor-not-allowed opacity-50"
    : isTenantMode 
      ? "bg-bgMain text-primary border-2 border-primary/50 hover:bg-primary/10 hover:border-primary active:scale-95"
      : "bg-bgMain text-primary border border-primary/30 hover:bg-primary hover:text-white hover:scale-105 active:scale-95"
}`}
 title={visuallyLocked 
 ? `${typeName} Interview is locked. Upgrade to premium to unlock.` 
 : `Start ${typeName} Interview`}
 >
 {visuallyLocked &&"🔒"}{typeName}
 </button>
 );
 }
 )}
</div>

 {/* Plan display - Hide for tenant */}
 {!isTenantMode && (
   <div className="text-center mt-4 space-y-6 pt-8 border-t border-accentGray/30">
     <div className="flex items-center justify-center gap-3">
       <span className="text-[10px] font-black text-muted uppercase tracking-widest">Active Plan</span>
       <span className="bg-primary/10 px-4 py-1.5 rounded-full border border-primary/30 text-primary font-black text-xs uppercase tracking-widest shadow-xl">
         {plan || 'Free'}
       </span>
     </div>
    
     {isPaid ? (
       <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-3xl animate-fadeIn">
         <p className="text-green-400 font-black text-xs uppercase tracking-widest mb-1">
           Premium Active
         </p>
         {expiry && (
           <p className="text-body/80 text-[10px] font-bold uppercase tracking-tighter">
              Expires {formatPlanDate(expiry)}
           </p>
         )}
       </div>
     ) : (
       <div className="space-y-4">
         {usage.mockInterviews >= (limits?.mockInterviews || 1) && (
           <p className="text-red-400 font-black text-[10px] uppercase tracking-widest">
             ⚠️ Free Limit Reached. Unlock unlimited access below.
           </p>
         )}
         <button
           onClick={() => navigate("/skillgenpayments")}
           className="btn-primary-gradient px-10 py-4 rounded-2xl font-black text-lg shadow-xl hover:scale-105 active:scale-95 transition-all uppercase tracking-widest w-full"
           title="Upgrade to Premium to unlock all interview types and features"
         >
           🚀 Upgrade to Premium
         </button>
       </div>
     )}
   </div>
 )}
 </div>
 </div>

 <PremiumUpgradeModal 
    isOpen={showModal} 
    onClose={() => setShowModal(false)}
    title="Unlock Unlimited Interviews"
    message={`You've used your free attempt. Upgrade your subscription to continue practicing with our AI-powered ${selectedType?.replace(/_/g," ")} interview.`}
 />
</div>

 );
}


